/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useState, useEffect, useRef } from 'react';
import { useConfig } from '../contexts/config';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import generator from '../utils/generator';
import { Link, Loading, Tabs, Tab, Select, SelectItem, TabPanels, TabPanel, TabList } from '@carbon/react';
import { decycle } from 'json-decycle';
import ViewableJSONDisplay from './ViewableJSONDisplay';
import ExpandableDisplay from './ExpandableDisplay';
import { get } from 'lodash';
import { cleanUpKey } from '../utils/common';
import Markdown from './Markdown';
import { getTranslatedExtensions, replaceDescriptionWithLanguage, getLanguageCodes } from '../utils/translate';
import ExternalLinkDecorator from './ExternalLinkDecorator';
import { useApi } from 'contexts/ApiHandler';
import { CodeViewer } from './CodeViewer';

const COMPLEX_JSON_SCHEMA_TYPES = ['object', 'array'];
const COMPLEX_AVRO_SCHEMA_TYPES = ['record', 'enum', 'map', 'fixed'];
const DEFAULT_HIGHLIGHT_LANGUAGE = 'json';
const HIGHLIGHT_LANGUAGE_TYPES = ['json', 'xml', 'yaml'];

const generateSchema = async (api, apiHandler, object, contentType) => {
  let result;
  let definitions = apiHandler.getSchemasFullPath(api);

  let schema = object;
  if (schema.schema) {
    schema = schema.schema;
    get(object, `content[${contentType}].schema`);
  } else if (get(object, `content[${contentType}].schema`)) {
    schema = schema.content[contentType].schema;
  }
  if (schema.$ref) {
    schema = apiHandler.getLocalReference(schema.$ref, api);
  }

  // Don't render "schema" for primitive data types
  if (schema.type && !COMPLEX_JSON_SCHEMA_TYPES.includes(schema.type) && !COMPLEX_AVRO_SCHEMA_TYPES.includes(schema.type)) {
    result = { schema: schema, isPrimitive: true };
  } else {
    // dereference schema using definitions, if definitions provided
    if (definitions && Object.keys(definitions).length > 0) {
      // flatten schema, add the schema to render to the schema definitions,
      // so the flattening can find any references into the definitions
      schema = JSON.parse(JSON.stringify(schema));
      replaceDescriptionWithLanguage(schema, getLanguageCodes()[0]);
      definitions.schema = schema;
      schema = await generator.generateFlattenedSchema(definitions);
    }
    result = { schema, isPrimitive: false };
  }

  return result;
};

const DefinitionDisplay = ({
  object,
  pathName,
  method,
  mode = 'both',
  type = 'operation',
  contentType = 'application/json',
  maxDepth,
  objectIdentifier,
  schemaName = null,
  options = {},
  viewMode,
  onMediaTypeChange
}) => {
  const { t } = useTranslation();
  const [schemaContent, updateSchemaContent] = useState(null);
  const [error, updateError] = useState(null);
  const [selectedExample, updateSelectedExample] = useState(undefined);
  const [examplesMap, updateExamples] = useState(null);
  const [example, updateExample] = useState(null);
  const [expandedExample, updateExpandedExample] = useState(null);
  const [expandedSchema, updateExpandedSchema] = useState(null);
  const [primitiveSchema, updatePrimitiveSchema] = useState(null);
  const [primitivePayload, updatePrimitivePayload] = useState(false);
  const [selected, updateSelected] = useState(0);
  const [copyableSchema, updateCopyableSchema] = useState(null);
  const [selectedMediaType, updateSelectedMediaType] = useState(null);
  const [selectedContentType, updateSelectedContentType] = useState(null);
  const [highlightLanguage, updateHighlightLanguage] = useState(DEFAULT_HIGHLIGHT_LANGUAGE);
  const [showMediaTypeSelector, updateShowMediaTypeSelector] = useState(false);
  const [mediaTypes, updateMediaTypes] = useState([]);
  const config = useConfig();

  const { api, apiHandler } = useApi();
  const _isMounted = useRef(false);
  useEffect(() => {
    _isMounted.current = true;

    return () => {
      _isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    updateError(false);
    updateExample(null);
    if (object.collectionFormat) {
      object.collectionFormat = null;
    }
    let showSelector = false;
    let contentTypes = [];
    if (type === 'response') {
      contentTypes = object.content && Object.keys(object.content).length > 1 ? Object.keys(object.content) : [];
      if (contentTypes.length > 1) {
        showSelector = true;
      }
    }
    let currentContentType = contentType;
    updateMediaTypes(contentTypes);
    updateShowMediaTypeSelector(showSelector);
    if (showSelector) {
      currentContentType = selectedMediaType && contentTypes.includes(selectedMediaType) ? selectedMediaType : contentTypes[0];
    }
    updateSelectedContentType(currentContentType);
    if (type === 'operation' && api && pathName && method && (mode === 'both' || mode === 'example')) {
      generator
        .generateExampleParameter({
          api: options.isCallback && options.operation ? options.operation : api,
          pathName: pathName,
          verb: method,
          parameter: object,
          contentTypeHeader: currentContentType,
          acceptHeader: currentContentType,
          skipBeautify: false,
          maxDepth: maxDepth,
          references: null,
          numItems: get(config, 'options.exampleArrayItems')
        })
        .then(
          (data) => {
            let example;
            if (data.hasMultipleExamples) {
              updateExamples(data.example);
              if (selectedExample && data.example[selectedExample]) {
                example = data.example[selectedExample];
              } else {
                example = Object.values(data.example)[0];
                updateSelectedExample(Object.keys(data.example)[0]);
              }
            } else {
              updateExamples(null);
              example = data.example || data.example === false ? data.example : data;
              if (typeof example === 'object' && 'value' in example) {
                example = JSON.stringify(example.value, decycle(), 2);
              } else if (Array.isArray(example) || typeof example === 'object') {
                example = JSON.stringify(example, decycle(), 2);
              }
            }
            updateExampleState(example, currentContentType);
          },
          (error) => {
            updateErrorState(error);
            console.error(error);
          }
        );
    } else if (type === 'response') {
      generator
        .generateExampleResponse({
          api: options.isCallback && options.operation ? options.operation : api,
          path: pathName,
          verb: method,
          response: api.openapi ? object : objectIdentifier,
          contentType: currentContentType,
          skipBeautify: false,
          maxDepth: maxDepth,
          references: null,
          config: { accepts: currentContentType },
          numItems: get(config, 'options.exampleArrayItems')
        })
        .then(
          (data) => {
            let example;
            if (data.hasMultipleExamples) {
              updateExamples(data.example);
              if (selectedExample && data.example[selectedExample]) {
                example = data.example[selectedExample];
              } else {
                example = Object.values(data.example)[0];
                updateSelectedExample(Object.keys(data.example)[0]);
              }
            } else {
              updateExamples(null);
              example = data.example || data.example === false ? data.example : data;
              if (typeof example === 'object' && 'value' in example) {
                example = JSON.stringify(example.value, decycle(), 2);
              } else if (Array.isArray(example) || typeof example === 'object') {
                example = JSON.stringify(example, decycle(), 2);
              }
            }
            updateExampleState(example, currentContentType);
          },
          (error) => {
            updateErrorState(error);
            console.error(error);
          }
        );
    }
    if (object && (object?.schema ||object?.enum ||(object?.content && Object.values(object.content).some(c => c.schema))) && (mode === 'both' || mode === 'schema')) {
      generateSchema(api, apiHandler, object, currentContentType).then(
        (schema) => {
          let isPrimitive = schema.isPrimitive;
          if (schema.schema) {
            schema = schema.schema;
          }
          updateSchemaState(schema, isPrimitive);
          updatePrimitiveSchema(isPrimitive);
        },
        (error) => updateErrorState(error)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    api,
    apiHandler,
    type,
    object,
    pathName,
    method,
    contentType,
    selectedMediaType,
    mode,
    maxDepth,
    objectIdentifier,
    options.isCallback,
    options.operation
  ]);

  useEffect(() => {
    let selectedHighlightLanguage = DEFAULT_HIGHLIGHT_LANGUAGE;
    HIGHLIGHT_LANGUAGE_TYPES.forEach((highlightLanguage) => {
      if (!selectedContentType && apiHandler.isSOAP(api)) {
        selectedHighlightLanguage = 'xml';
      } else if (selectedContentType && selectedContentType.indexOf(highlightLanguage) >= 0) {
        selectedHighlightLanguage = highlightLanguage;
      }
    });
    updateHighlightLanguage(selectedHighlightLanguage);
  }, [selectedContentType, apiHandler, api]);

  const updateErrorState = (error) => {
    if (_isMounted.current) {
      updateError(error);
      updateExpandedExample(false);
      updateExpandedSchema(false);
    }
  };

  const updateExampleState = (examplePayload, contentType) => {
    if (_isMounted.current) {
      let parsedExamplePayload = examplePayload;
      // If no contentType has been provided, we'll assume that the highlightLanguage type is JSON, and attempt to
      // convert the string to an object. If this fails, we'll just serve up the original payload
      // and set the primitivePayload state boolean which will ensure that the payload isn't rendered
      //  by the JSON viewer.
      //
      // As the example generator can return a string of null, we also have to check for this because
      // passing that through the JSON parse will remove the quotes and treat the payload as a null object.
      // This then means that loading icon is never removed, because the example check will always be null.
      if (examplePayload !== 'null' && (!contentType || contentType === 'application/json')) {
        try {
          parsedExamplePayload = JSON.parse(examplePayload);
        } catch (err) {
          // NOP - Just use the default payload if we can't parse the output. It might be
          //      json, but a primitive payload.
        }
      }
      updatePrimitivePayload(typeof parsedExamplePayload !== 'object');
      updateExample(parsedExamplePayload);
      updateExpandedExample(false);
      updateExpandedSchema(false);
    }
  };

  const updateSchemaState = (schema, isPrimitive) => {
    if (schema.schema) {
      schema = schema.schema;
    }
    if (_isMounted.current && (!isPrimitive || schema.enum)) {
      let copyableSchema = JSON.stringify(schema, decycle(), 2);
      try {
        updateSchemaContent(schema);
      } catch (e) {
        console.error(e);
      }
      updateCopyableSchema(copyableSchema);
      updateExpandedExample(false);
      updateExpandedSchema(false);
    }
  };

  const expandExample = () => {
    updateExpandedExample(!expandedExample);
    updateSelected(0);
  };

  const expandSchema = () => {
    updateExpandedSchema(!expandedSchema);
    updateSelected(1);
  };

  // some types may cause example generator to return the string "null" - handle this by showing no example
  let isExampleNull = example === 'null';
  if (isExampleNull && !schemaContent) {
    return null;
  }

  let copyableContent = highlightLanguage === 'json' && !primitivePayload ? JSON.stringify(example, null, 2) : null;

  let markupType = apiHandler.getMarkupType();

  let alabel = object.name ? `definition-display-tab-${cleanUpKey(object.name)}` : 'definition-display-tab';

  const renderExternalDocs = (
    <>
      {object.schema && object.schema.externalDocs && (
        <div className="schemaDocs">
          {object.schema.externalDocs.description && (
            <Markdown className={'externalLinkDescription'} data={getTranslatedExtensions(object.schema.externalDocs, 'description')} markupType={markupType} />
          )}
          <Link href={object.schema.externalDocs.url} target="_blank" rel="noopener noreferrer">
            {object.schema.externalDocs.url}
            <ExternalLinkDecorator />
          </Link>
        </div>
      )}
    </>
  );

  const renderCodeSnippet = (example, highlightLanguage, type) => {
    return highlightLanguage === 'json' && !primitivePayload ? (
      <ViewableJSONDisplay content={example} />
    ) : (
      <CodeViewer data-testid={`${type}-example-generated`} codeLanguage={highlightLanguage}>
        {String(example)}
      </CodeViewer>
    );
  };

  const onExampleChange = (evt, index) => {
    let example = examplesMap[evt.target.value];
    updateSelectedExample(evt.target.value);
    if (typeof example === 'object' && 'value' in example) {
      example = JSON.stringify(example, decycle(), 2);
    } else if (Array.isArray(example) || typeof data === 'object') {
      example = JSON.stringify(example, decycle(), 2);
    }
    updateExampleState(example, contentType);
  };
  const prefix = type === 'response' ? 'response' : object?.name + '-' + object.in;

  return (
    <>
      <div className="definition-selectors">
        {showMediaTypeSelector && (
          <div>
            <p className="media-type-selector-header">Media Type</p>
            <Select
              onChange={(e) => {
                updateSelectedMediaType(e.target.value);
              }}
              data-testid="media-type-selector"
              hideLabel
              className="media-type-selector"
              id={prefix + '-media-type-selector'}
            >
              {mediaTypes.map((contentType) => {
                return <SelectItem text={contentType} key={contentType} value={contentType} />;
              })}
            </Select>
          </div>
        )}
        {examplesMap && (
          <div>
            <p className="examples-selector-header">{t('Examples')}</p>
            <Select
              data-testid="example-selector"
              hideLabel
              className="example-selector"
              onChange={onExampleChange}
              value={selectedExample}
              id={prefix + '-example-selector'}
            >
              {Object.keys(examplesMap).map((key) => {
                return <SelectItem key={key} text={key} value={key} />;
              })}
            </Select>
          </div>
        )}
      </div>
      <div className="definitionDisplay">
        <Tabs className="definition--tabs" selected={selected} tabContentClassName="tab-content" aria-label={alabel}>
          <TabList aria-label="definition tabs">
            {/* To be able to display a parameter example, the example generator requires these three fields */}
            {[
              (type === 'operation' || type === 'response') && api && pathName && method && !isExampleNull && !error && (
                <Tab
                  key="example"
                  id={type === 'response' ? 'response_example' : `${object.in}_${object.name}_example`}
                  title={t('example')}
                  data-testid={'example-tab'}
                >
                  {t('example')}
                </Tab>
              ),
              error && (
                <Tab key="error" id={type === 'response' ? 'response_error' : `${object.in}_${object.name}_error`} title={t('error')} data-testid="error-tab">
                  {t('error')}
                </Tab>
              ),
              // If the object to display has a schema, show the schema tab too
              schemaContent && !primitiveSchema && (
                <Tab
                  key="schema"
                  id={type === 'response' ? 'response_schema' : `${object.in}_${object.name}_schema`}
                  title={t('schema')}
                  data-testid={'schema-tab'}
                >
                  {t('schema')}
                </Tab>
              )
            ].filter(Boolean)}
          </TabList>
          <TabPanels>
            {[
              (type === 'operation' || type === 'response') && api && pathName && method && !isExampleNull && !error && (
                <TabPanel key="operation">
                  <ExpandableDisplay expanded={expandedExample} copyableContent={copyableContent} onExpand={expandExample}>
                    {example === null && <Loading small withOverlay={false} data-testid={'loading-example'} />}
                    {example !== null && renderCodeSnippet(example, highlightLanguage, type)}
                  </ExpandableDisplay>
                </TabPanel>
              ),
              error && (
                <TabPanel key="error">
                  {' '}
                  <div data-testid="generator-error">{t('generator_error', { error: error })}</div>
                </TabPanel>
              ),
              schemaContent && !primitiveSchema && (
                <TabPanel key="schema">
                  <ExpandableDisplay copyableContent={copyableSchema} expanded={expandedSchema} onExpand={expandSchema}>
                    {viewMode === 'schema' && <ViewableJSONDisplay content={schemaContent} jsonName={schemaName} />}
                    {viewMode !== 'schema' && <ViewableJSONDisplay content={schemaContent} jsonName={schemaName} />}
                    {renderExternalDocs}
                  </ExpandableDisplay>
                </TabPanel>
              )
            ].filter(Boolean)}
          </TabPanels>
        </Tabs>
      </div>
    </>
  );
};

DefinitionDisplay.propTypes = {
  object: PropTypes.object,
  objectIdentifier: PropTypes.string,
  api: PropTypes.object,
  pathName: PropTypes.string,
  method: PropTypes.string,
  type: PropTypes.string,
  streamName: PropTypes.string,
  contentType: PropTypes.string,
  mode: PropTypes.string,
  maxDepth: PropTypes.number
};

export default DefinitionDisplay;
